import React, { useEffect, useState } from "react";
import { Grid, Tooltip as MuiTooltip, Paper, Box, MenuItem, Divider } from '@mui/material';
import { InfoOutlined } from "@mui/icons-material";
import { capitalize, orderBy } from "lodash";
import { CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getSiteTrending } from '../../../actions/internal/SiteActions';
import Colors from "../../../constants/Colors";
import GenericInfoCard from "./cards/GenericInfoCard";
import { getSiteTrendingByIdAndRange } from '../../../selectors/SiteSelectors';
import StyledSubHeader from '../../common/StyledSubHeader';
import { StyledLoadingSection } from '../../common/StyledProgressCircle';
import { CARD_SUB_HEADER_STYLE } from '../../../constants/Styles';
import { ContainedButton, TabButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router';
import { getUserId } from "../../../selectors/UserSelectors";
import { calculateStartDate, formatDateToYYYYMMDD } from "../../../utilities/DateUtilities";
import StyledPercentageChip from "../../common/StyledPercentageChip";
import { StyledDropDown } from "../../common/StyledDropDown";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";
import StyledLineItem from "../../common/StyledLineItem";

const PastDayChoices = {
    PAST_7_DAYS: 7,
    PAST_30_DAYS: 30,
    PAST_60_DAYS: 60,
    PAST_90_DAYS: 90,
    PAST_120_DAYS: 120
}

const PastDayChoicesLabels = {
    [PastDayChoices.PAST_7_DAYS]: 'Last 7 Days',
    [PastDayChoices.PAST_30_DAYS]: 'Last 30 Days',
    [PastDayChoices.PAST_60_DAYS]: 'Last 60 Days',
    [PastDayChoices.PAST_90_DAYS]: 'Last 90 Days',
    [PastDayChoices.PAST_120_DAYS]: 'Last 120 Days'
}

const PageViews = {
    ANALYTICS: 'ANALYTICS',
    TRENDING: 'TRENDING',
    INSIGHTS: 'INSIGHTS'
}

const IMPRESSIONS_INFO = "Impressions show how many times your site or page appeared in search results.";
const CLICKS_INFO = "Clicks show how many times someone actually clicked on your site from an impression.";
const AVG_CTR_INFO = "Average CTR (Click-Through Rate) is the percentage of impressions that turned into clicks. In other words, how often people chose your site after seeing it";

const ViewSiteTrendingComponent = ({ siteId, dateRange }) => {
    const [ isLoading, setIsLoading ] = useState(true);

    const trending = getSiteTrendingByIdAndRange(siteId, dateRange)

    useEffect(() => {
        prepareSiteSession();
    }, [siteId, dateRange]);
    
    const prepareSiteSession = async () => {
        try {
            setIsLoading(true);
            const userId = getUserId();
            await fetchSiteAnalytics(siteId, userId);
        } catch (ex) {
            console.error("Error fetching analytics:", ex);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSiteAnalytics = async (siteId, userId) => {
        const date = new Date();
        const startDate = calculateStartDate(date.getFullYear(), date.getMonth() + 1, date.getDate(), dateRange);
        const endDate = formatDateToYYYYMMDD(date);
        const defaultOptions = { startDate, endDate, includeRecentReports: true };
        await getSiteTrending(userId, siteId, defaultOptions, dateRange);
      };

    const cleanUrl = (url) => {
        try {
          const parsed = new URL(url);
          // Remove trailing slash and return hostname + pathname
          let path = parsed.pathname.replace(/\/$/, ""); 
          return parsed.hostname + (path !== "/" ? path : "");
        } catch {
          return url; // fallback if it's not a valid URL
        }
      }

    const renderLoadingContent = () => (
        <StyledLoadingSection />
    )

    const renderPercentageSection = (current_number, past_number, flip_value = false) => {
        if (past_number === 0) return renderPercentageIcon(0, flip_value);
        // const color = isNegative ? Colors.redColor : Colors.greenColor;
        let percentChange = current_number - past_number;
        // if (isNegative) percentChange *= -1;
        percentChange = percentChange / past_number * 100;

        return renderPercentageIcon(percentChange, flip_value)
    }

    const renderPercentageIcon = (percentage, flip_value = false) => {
        // const color = isNegative ? Colors.redColor : Colors.greenColor;
        return (
            <StyledPercentageChip value={(percentage).toFixed(1)} flip={flip_value} />
        )
    }

    const renderOverviewSection = () => (
        <Grid container justify='flex-start' spacing={2} sx={{ marginTop: '6px' }}>
            <GenericInfoCard title={"Impressions"} content={trending.queries.totalImpressions} subcontent={renderPercentageSection(trending.queries.totalImpressions, trending.queries.pastTotals.impressions)} footer={`over the ${PastDayChoicesLabels[dateRange]}`.toLocaleLowerCase()} info={IMPRESSIONS_INFO}/>
            <GenericInfoCard title={"Clicks"} content={trending.queries.totalClicks} subcontent={renderPercentageSection(trending.queries.totalClicks, trending.queries.pastTotals.clicks)} footer={`over the ${PastDayChoicesLabels[dateRange]}`.toLocaleLowerCase()} info={CLICKS_INFO}/>
            <GenericInfoCard title={"Average CTR"} content={`${(trending.queries.avgCTR *100).toFixed(2)}%`} subcontent={renderPercentageSection((trending.queries.avgCTR), (trending.queries.pastTotals.avgCTR))} footer={`over the ${PastDayChoicesLabels[dateRange]}`.toLocaleLowerCase()} info={AVG_CTR_INFO}/>
        </Grid>
    )

    const renderTopPerformanceSection = () => (
        <Grid container justify='flex-start' spacing={2}>
            {renderTrendingQueriesSection()}
            {renderTrendingPagesSection()}
        </Grid>
    )

    const renderTrendingQueriesSection = () => (
        <Grid item xs={12} md={6}>
            <StyledSection title="Top Queries by Impressions">
                {trending.queries.topQueries.slice(0,10).map((q, i) => (
                    <div key={q.keys[0]}>
                        <StyledLineItem
                            sx={{ paddingTop: '8px', paddingBottom: '8px'}}
                            primary={q.keys[0]}
                            secondary={q.impressions}
                        />
                        { i !== 9 && <Divider /> }
                    </div>
                ))}
                
            </StyledSection>
        </Grid>
    )

    const renderTrendingPagesSection = () => (
        <Grid item xs={12} md={6}>
            <StyledSection title="Trending Page by Clicks">
                {trending.pages.topPagesByClicks.slice(0,10).map((q, i) => (
                    <div key={q.keys[0]}>
                        <StyledLineItem
                            sx={{ paddingTop: '8px', paddingBottom: '8px'}}
                            primary={cleanUrl(q.keys[0])}
                            secondary={q.clicks}
                        />
                        { i !== 9 && <Divider /> }
                    </div>
                ))}
            </StyledSection>
        </Grid>
    )

    const renderSetupContent = () => {
        return (
            <Grid container justifyContent="center" sx={{ marginTop: '10%'}}>
                <Grid item xs="auto">
                    <Paper sx={{ padding: '20px', maxWidth: '600px' }}>
                        <h4 style={{ marginTop: '10px', marginBottom: '10px'}}>Not Enabled...</h4>
                        <p>Trending reports are not currently available for this account. If you'd like to get them set up, please let us know!</p>
                        <Box sx={{ marginTop: '50px', display: 'flex' }}>
                            <ContainedButton sx={{ marginRight: '10px' }} onClick={() => window.open('https://consultwithkyle.com/contact-us', '_blank')}>
                                Contact Us
                            </ContainedButton>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        )
    }

    const renderProperView = () => {
        if (isLoading) {
            return renderLoadingContent();
        } else if (!trending || !trending.enabled) {
            return renderSetupContent();
        } else {
            return (
                <>
                    {renderOverviewSection()}
                    {renderTopPerformanceSection()}
                </>
            )
        }
    }
    
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                {renderProperView()}
            </Grid>
            
        </Grid>
    )
}

export default withRouter(ViewSiteTrendingComponent);


/*
        <Grid container>
          <Grid item xs={12} sm={10} md={9} sx={{ marginTop: '16px' }}>
            <Grid container justifyContent="space-between" >
                <Grid item xs='auto'>
                    <StyledSubHeader title="Activity" />
                </Grid>
                <Grid item xs='auto'>
                    <StyledDropDown
                        label="Date Range"
                        value={pastDayView}
                        onChange={(e) => changeDateRange(e.target.value)}
                        sx={{ minWidth: '250px'}}
                    >
                        {Object.keys(PastDayChoicesLabels).map(key => (
                            <MenuItem
                                key={key}
                                value={key}
                            >
                                {PastDayChoicesLabels[key]}
                            </MenuItem>
                        ))}
                    </StyledDropDown>
                </Grid>
            </Grid>
            {renderProperView()}
          </Grid>
      </Grid>
*/